import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCategoriesAndSubcategories } from '../../../services/productTemplates';
import { ClientSideEndpoints } from '../../../services/clientDetailService';

export const fetchCategoriesAndSubcategories = createAsyncThunk(
    'templates/fetchCategoriesAndSubcategories',
    async (_, { rejectWithValue }) => {
        try {
            const data = await getCategoriesAndSubcategories();
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || 'Something went wrong');
        }
    }
);

export const fetchInfosForHomePage = createAsyncThunk(
    'products/fetchInfosForHomePage',
    async (_, { rejectWithValue }) => {
        try {
            const data = await ClientSideEndpoints.getDetailsForHomePage();
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || 'Something went wrong');
        }
    }
);

// NEW: Enhanced homepage data with most customized section
export const fetchEnhancedHomePage = createAsyncThunk(
    'products/fetchEnhancedHomePage',
    async (_, { rejectWithValue }) => {
        try {
            const data = await ClientSideEndpoints.getEnhancedDetailsForHomePage();
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || 'Something went wrong');
        }
    }
);

// NEW: Most customized section only
export const fetchMostCustomizedSection = createAsyncThunk(
    'products/fetchMostCustomizedSection',
    async (_, { rejectWithValue }) => {
        try {
            const data = await ClientSideEndpoints.getMostCustomizedSection();
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || 'Something went wrong');
        }
    }
);

const homeSlice = createSlice({
    name: 'home',

    initialState: {
        categories: [],
        customer_comments: [],
        filtered_products: [],
        subcategoriesById: {},
        // NEW: Most customized section data
        most_customized_products: [],
        recent_customizations: [],
        customization_stats: {
            total_customizations: 0,
            this_week_customizations: 0,
            active_customizable_products: 0
        },
        loading: false,
        error: null,
        categoriesFetched: false,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCategoriesAndSubcategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCategoriesAndSubcategories.fulfilled, (state, action) => {
                state.categoriesFetched = true;
                state.loading = false;
                state.categories = action.payload.map((category) => ({
                    id: category.id,
                    name: category.category_name,
                    description: category.category_description,
                    image: category.product_image,
                    subcategories: category.sub_categories.map((sub) => ({
                        id: sub.id,
                        name: sub.sub_category_name,
                        description: sub.sub_category_description,
                    })),
                }));

                // Flatten subcategories for quick lookup
                state.subcategoriesById = action.payload.reduce((acc, category) => {
                    category.sub_categories.forEach((sub) => {
                        acc[sub.id] = {
                            id: sub.id,
                            name: sub.sub_category_name,
                            categoryId: category.id,
                        };
                    });
                    return acc;
                }, {});
            })
            .addCase(fetchCategoriesAndSubcategories.rejected, (state, action) => {
                state.loading = false;
                state.categoriesFetched = false;
                state.error = action.payload;
            })
            .addCase(fetchInfosForHomePage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchInfosForHomePage.fulfilled, (state, action) => {
                state.loading = false;
                state.filtered_products = action.payload.featured_products.map((product) => ({
                    id: product.id,
                    name: product.product_name,
                    price: product.price,
                    stockLevel: product.stock_level,
                    status: product.status,
                    stock: product.stock,
                    isFeatured: product.is_featured,
                    category: {
                        id: product.category.id,
                        name: product.category.name,
                    },
                    subCategory: {
                        id: product.sub_category.id,
                        name: product.sub_category.name,
                    },
                    averageRating: product.average_rating,
                    front_side_image: product.images.front_image,
                    back_side_image: product.images.back_image,
                }));

                state.customer_comments = action.payload.customers_comments.map((comment) => ({
                    id: comment.id,
                    name: comment.name,
                    comment: comment.comment,
                    profilePicture: comment.profile_picture,
                }));
            })
            .addCase(fetchInfosForHomePage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // NEW: Enhanced homepage handlers
            .addCase(fetchEnhancedHomePage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchEnhancedHomePage.fulfilled, (state, action) => {
                state.loading = false;
                
                // Handle featured products (same as before)
                state.filtered_products = action.payload.featured_products.map((product) => ({
                    id: product.id,
                    name: product.product_name,
                    price: product.price,
                    stockLevel: product.stock_level,
                    status: product.status,
                    stock: product.stock,
                    isFeatured: product.is_featured,
                    category: {
                        id: product.category.id,
                        name: product.category.name,
                    },
                    subCategory: {
                        id: product.sub_category.id,
                        name: product.sub_category.name,
                    },
                    averageRating: product.average_rating,
                    front_side_image: product.images.front_image,
                    back_side_image: product.images.back_image,
                }));

                // Handle customer comments (same as before)
                state.customer_comments = action.payload.customers_comments.map((comment) => ({
                    id: comment.id,
                    name: comment.name,
                    comment: comment.comment,
                    profilePicture: comment.profile_picture,
                }));

                // NEW: Handle most customized products
                state.most_customized_products = action.payload.most_customized_products.map((product) => ({
                    id: product.id,
                    name: product.product_name,
                    price: product.price,
                    stockLevel: product.stock_level,
                    status: product.status,
                    stock: product.stock,
                    isFeatured: product.is_featured,
                    category: {
                        id: product.category.id,
                        name: product.category.name,
                    },
                    subCategory: {
                        id: product.sub_category.id,
                        name: product.sub_category.name,
                    },
                    averageRating: product.average_rating,
                    front_side_image: product.images.front_image,
                    back_side_image: product.images.back_image,
                    customization_count: product.customization_count || 0,
                    badge: product.badge || 'Ready to Customize'
                }));

                // NEW: Handle recent customizations
                state.recent_customizations = action.payload.recent_customizations || [];

                // NEW: Handle customization stats
                state.customization_stats = action.payload.customization_stats || {
                    total_customizations: 0,
                    this_week_customizations: 0,
                    active_customizable_products: 0
                };
            })
            .addCase(fetchEnhancedHomePage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // NEW: Most customized section only handlers
            .addCase(fetchMostCustomizedSection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMostCustomizedSection.fulfilled, (state, action) => {
                state.loading = false;
                
                state.most_customized_products = action.payload.most_customized_products.map((product) => ({
                    id: product.id,
                    name: product.product_name,
                    price: product.price,
                    stockLevel: product.stock_level,
                    status: product.status,
                    stock: product.stock,
                    isFeatured: product.is_featured,
                    category: {
                        id: product.category.id,
                        name: product.category.name,
                    },
                    subCategory: {
                        id: product.sub_category.id,
                        name: product.sub_category.name,
                    },
                    averageRating: product.average_rating,
                    front_side_image: product.images.front_image,
                    back_side_image: product.images.back_image,
                    customization_count: product.customization_count || 0,
                    badge: product.badge || 'Ready to Customize'
                }));

                state.recent_customizations = action.payload.recent_customizations || [];
                state.customization_stats = action.payload.customization_stats || {
                    total_customizations: 0,
                    this_week_customizations: 0,
                    active_customizable_products: 0
                };
            })
            .addCase(fetchMostCustomizedSection.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export default homeSlice.reducer;